import {
  Box, Typography, Table, TableBody, TableContainer,
  TableHead, Button, useTheme,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";

export const RCell = ({ children, head = false, align = "left", width, style = {} }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;
  const text   = theme.palette.neutral.dark;

  return (
    <td
      className={head ? "rh-cell" : undefined}
      style={{
        padding: "9px 14px",
        fontSize: head ? "11px" : "13px",
        fontWeight: head ? 600 : 400,
        letterSpacing: head ? "0.07em" : "normal",
        textTransform: head ? "uppercase" : "none",
        textAlign: align,
        color: head ? accent : text,
        backgroundColor: head
          ? (isDark ? "rgba(29,158,117,0.08)" : "rgba(29,158,117,0.06)")
          : "transparent",
        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
        width: width || "auto",
        whiteSpace: head ? "nowrap" : "normal",
        ...style,
      }}
    >
      {children}
    </td>
  );
};

export const RRow = ({ children, index }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isEven = index % 2 === 0;

  return (
    <tr
      style={{
        backgroundColor: isEven
          ? "transparent"
          : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"),
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? "rgba(29,158,117,0.07)" : "rgba(29,158,117,0.05)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isEven ? "transparent" : (isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)"); }}
    >
      {children}
    </tr>
  );
};

/* ── Main ReportTable ── */
const ReportTable = ({
  columns,           // [{ label, align, width }]
  rows,              // full data array
  renderRow,         // (row, index) => <RRow>...</RRow>
  emptyMessage = "No data found",
  loading = false,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
}) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";
  const bodyBg = theme.palette.background.alt;
  const muted  = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.15)";

  return (
    <Box sx={{
      backgroundColor: bodyBg,
      border: `1px solid ${border}`,
      borderRadius: "12px",
      overflowX: "auto",
    }}>
      <TableContainer>
        <Table style={{ width: "100%", borderCollapse: "collapse" }}>
          <TableHead>
            <tr>
              {columns.map((col, i) => (
                <RCell key={i} head align={col.align || "left"} width={col.width}>
                  {col.label}
                </RCell>
              ))}
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", padding: "3rem" }}>
                  <Typography sx={{ fontSize: "13px", color: muted }}>Loading...</Typography>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", padding: "3rem" }}>
                  <TableChartIcon sx={{ fontSize: "36px", color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", mb: "8px", display: "block", mx: "auto" }} />
                  <Typography sx={{ fontSize: "13px", color: muted }}>{emptyMessage}</Typography>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => renderRow(row, index))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", py: "1rem", borderTop: `1px solid ${border}` }}>
          <Button
            onClick={onLoadMore}
            disabled={loadingMore}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1.25rem", py: "0.45rem", borderRadius: "8px",
              textTransform: "none",
              color: theme.palette.secondary.main,
              border: `1px solid ${isDark ? "rgba(29,158,117,0.3)" : "rgba(29,158,117,0.35)"}`,
              "&:hover": { backgroundColor: isDark ? "rgba(29,158,117,0.1)" : "rgba(29,158,117,0.06)" },
            }}
          >
            {loadingMore ? "Loading..." : "View More Records"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReportTable;
